import styles from "./style.module.scss";
import { translate } from "@helpers/translationObjectFormatter";
import {
    ICompareCategoryProps,
    TranslationsObject,
} from "@genericTypes/sharedTypes";
import Vendor from "./vendor";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { GQLOffers, GQLUrls } from "@gql/types/gql";
import { removeWordFromString } from "@helpers/removeWordFromString";
import { resolveURL } from "@lib/sharedUtils";
import LinkButton from "@components/shared/form/linkButton";

export default function CompareList({
    comparisonList,
}: {
    comparisonList: GQLOffers[];
    offersBySlug?: GQLOffers[];
}) {
    const [isChecked, setIsChecked] = useState(false);
    const [listToAdd, setListToAdd] = useState([
        ...comparisonList.map((el) => removeWordFromString(el.slug as string)),
    ]);
    const [loading, setLoading] = useState(false);
    const [queryString, setQueryString] = useState("");
    const { data: props } = useQuery<ICompareCategoryProps, Error>([
        "compareCategoryProps",
    ]);
    const { category, allCategoryOffers: domainOffers } = props ?? {};
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);

    useEffect(() => {
        if (listToAdd.length < ((domainOffers as GQLOffers[])?.length ?? 0)) {
            setIsChecked(false);
        } else {
            setIsChecked(true);
        }
        setQueryString(
            listToAdd
                .sort((a, b) => {
                    if (typeof a === "string" && typeof b === "string")
                        return a.localeCompare(b);

                    return 0;
                })
                .join("-vs-"),
        );
    }, [listToAdd]);

    const unSelectedBuyers = domainOffers?.filter((el) => {
        const elementFound = comparisonList.find(
            (innerEl) =>
                removeWordFromString(innerEl.slug as string) ===
                removeWordFromString(el?.slug as string),
        );
        return !elementFound;
    });
    const elementsToShow = useMemo(
        () => [...comparisonList, ...(unSelectedBuyers ?? [])],
        [],
    );

    const changeCheckBoxHandler = (value: boolean) => {
        if (value) {
            setIsChecked(true);

            setListToAdd(
                (domainOffers as GQLOffers[])?.map((el) =>
                    removeWordFromString(el?.slug as string),
                ),
            );
        } else {
            setIsChecked(false);

            setListToAdd([]);
        }
    };

    return (
        <section
            className={`${styles["allSelectedSticky"]} lg:py-20 text-center lg:px-15 pt-4 px-10`}
        >
            <h2 className="  font-bold text-center lg:leading-[45px] lg:text-3xl mt-5 lg:mt-0 text-xl leading-7 ">
                {translate(
                    translations,
                    "several_comparePage_chooseProductsCompare",
                )}
            </h2>
            <h3
                className={` text-center lg:text-lg text-sm leading-5 lg:leading-8 ${styles["paragraphColor"]} mt-1 lg:mt-0`}
            >
                {translate(
                    translations,
                    "several_comparePage_chooseVendorsComparisons",
                )}
            </h3>
            <div className="mt-5 text-left ml-1">
                <input
                    type="checkBox"
                    id="input"
                    checked={isChecked}
                    onChange={(e) => changeCheckBoxHandler(e.target.checked)}
                />
                <label
                    className=" lg:text-base text-sm leading-4 lg:leading-5 ms-2"
                    htmlFor="input"
                >
                    {translate(translations, "several_comparePage_selectAll")}
                </label>
                <span
                    className={`${styles["itemsSelectedColor"]}  lg:text-base text-sm leading-5 ms-8`}
                >
                    <span
                        className={`${styles["counterColor"]} font-bold me-1`}
                    >
                        {listToAdd.length}
                    </span>

                    {translate(
                        translations,
                        "several_comparePage_ItemsSelected",
                    )}
                </span>
            </div>
            <div
                className={`flex flex-wrap justify-center lg:gap-4 gap-3 mt-5 overflow-y-auto ${styles["fixedHeight"]}`}
            >
                {elementsToShow?.map((el) => (
                    <div
                        key={el?.id}
                        onClick={() => {
                            const clickedElement = listToAdd.includes(
                                removeWordFromString(el?.slug as string),
                            );

                            if (clickedElement) {
                                setListToAdd((prev) =>
                                    prev.filter(
                                        (slug) =>
                                            removeWordFromString(slug) !==
                                            removeWordFromString(
                                                el?.slug as string,
                                            ),
                                    ),
                                );
                            } else {
                                setListToAdd((prev) => [
                                    ...prev,
                                    removeWordFromString(el?.slug as string),
                                ]);
                            }
                        }}
                    >
                        {el && <Vendor vendor={el} listToAdd={listToAdd} />}
                    </div>
                ))}
            </div>
            <div className="bg-white lg:pt-8 ">
                <LinkButton
                    href={`${resolveURL(
                        category?.url as GQLUrls,
                    )}/comparison/${queryString}`}
                    className={`${styles["stickyButton"]} font-bold rounded-none lg:rounded-lg text-lg leading-5 py-[13px] `}
                    onClick={() => setLoading(true)}
                    loading={loading}
                    disabled={loading}
                >
                    {translate(translations, "several_general_compareButton")}
                </LinkButton>
            </div>
        </section>
    );
}
